import { supabase } from "../utils/supabaseClient";
import {
  BillingCycle,
  Invoice,
  OwnerType,
  PaymentMethod,
  PlanTier,
  ResourceAddonPack,
  ResourceSummary,
  ResourceType,
  ResourceUsage,
  SubscriptionAddon,
  SubscriptionRecord,
  SubscriptionStatus,
  SubscriptionSummary,
  SubscriptionUser,
} from "../types/subscription";

// Get plan tiers
export async function getAvailablePlans(): Promise<PlanTier[]> {
  const { data, error } = await supabase.rpc("get_available_plans");

  if (error) {
    console.error("Error fetching plans:", error);
    throw error;
  }

  return data as PlanTier[];
}

// Get resource types
export async function getResourceTypes(): Promise<ResourceType[]> {
  const { data, error } = await supabase
    .rpc("get_resource_types");

  if (error) {
    console.error("Error fetching resource types:", error);
    throw error;
  }

  return data as ResourceType[];
}

// Get user subscription
export async function getUserSubscription(
  userId?: string
): Promise<SubscriptionRecord | null> {
  try {
    const { data, error } = await supabase
      .rpc("get_user_subscription", {
        user_id: userId || null
      });

    if (error) throw error;

    if (!data || data.length === 0) {
      return null;
    }

    return data[0] as SubscriptionRecord;
  } catch (error) {
    console.error("Error fetching user subscription:", error);
    return null;
  }
}

// Get organization subscription
export async function getOrganizationSubscription(
  orgId: string
): Promise<SubscriptionRecord | null> {
  const { data, error } = await supabase.rpc("get_organization_subscription", {
    org_id: orgId
  });

  if (error) {
    if (error.code === "PGRST116") {
      // No subscription found
      return null;
    }
    console.error("Error fetching organization subscription:", error);
    throw error;
  }

  if (!data || data.length === 0) {
    return null;
  }

  return data[0] as SubscriptionRecord;
}

// Get resource usage for a subscription
export async function getResourceUsage(
  subscriptionId: string
): Promise<ResourceUsage[]> {
  const { data, error } = await supabase
    .rpc("get_resource_usage", {
      sub_id: subscriptionId,
    });

  if (error) {
    console.error("Error fetching resource usage:", error);
    throw error;
  }

  return data as ResourceUsage[];
}

// Get subscription summary with resource usage
export async function getSubscriptionSummary(
  subscriptionId: string
): Promise<SubscriptionSummary | null> {
  const { data, error } = await supabase
    .rpc("get_subscription_usage_summary", {
      subscription_id: subscriptionId,
    });

  if (error) {
    console.error("Error fetching subscription summary:", error);
    throw error;
  }

  return data as SubscriptionSummary;
}

// Get subscription addons
export async function getSubscriptionAddons(
  subscriptionId: string
): Promise<SubscriptionAddon[]> {
  const { data, error } = await supabase.rpc("get_subscription_addons", {
    sub_id: subscriptionId,
  });

  if (error) {
    console.error("Error fetching subscription addons:", error);
    throw error;
  }

  return data as SubscriptionAddon[];
}

// Get available addon packs
export async function getAvailableAddonPacks(): Promise<ResourceAddonPack[]> {
  const { data, error } = await supabase.rpc("get_available_addon_packs");

  if (error) {
    console.error("Error fetching addon packs:", error);
    throw error;
  }

  return data as ResourceAddonPack[];
}

// Get subscription payment methods
export async function getPaymentMethods(
  subscriptionId: string
): Promise<PaymentMethod[]> {
  const { data, error } = await supabase.rpc("get_payment_methods", {
    sub_id: subscriptionId,
  });

  if (error) {
    console.error("Error fetching payment methods:", error);
    throw error;
  }

  return data as PaymentMethod[];
}

// Get subscription invoices
export async function getInvoices(subscriptionId: string): Promise<Invoice[]> {
  const { data, error } = await supabase.rpc("get_invoices", {
    sub_id: subscriptionId,
  });

  if (error) {
    console.error("Error fetching invoices:", error);
    throw error;
  }

  return data as Invoice[];
}

// Create or update subscription
export async function createOrUpdateSubscription(
  subscription: Partial<SubscriptionRecord>
): Promise<SubscriptionRecord> {
  const { data, error } = await supabase
    .rpc("create_subscription", {
      p_owner_id: subscription.owner_id,
      p_owner_type: subscription.owner_type,
      p_plan_id: subscription.plan_id,
      p_status: subscription.status || 'active',
      p_starts_at: subscription.starts_at || null,
      p_ends_at: subscription.ends_at || null,
    });

  if (error) {
    console.error("Error creating/updating subscription:", error);
    throw error;
  }

  return data?.[0] || data;
}

// Cancel subscription
export async function cancelSubscription(
  subscriptionId: string,
  cancelAtPeriodEnd: boolean = true
): Promise<SubscriptionRecord> {
  // For now, we'll use a simple status update since the current schema doesn't have cancel_at_period_end
  const { data, error } = await supabase.rpc("update_subscription_status", {
    sub_id: subscriptionId,
    new_status: cancelAtPeriodEnd ? 'active' : 'canceled',
  });

  if (error) {
    console.error("Error canceling subscription:", error);
    throw error;
  }

  return data?.[0] || data;
}

// Change subscription plan
export async function changeSubscriptionPlan(
  subscriptionId: string,
  planId: string
): Promise<SubscriptionRecord> {
  const { data, error } = await supabase.rpc("update_subscription_plan", {
    subscription_id: subscriptionId,
    new_plan_id: planId
  });

  if (error) {
    console.error("Error changing subscription plan:", error);
    throw error;
  }

  return data as SubscriptionRecord;
}

// Change billing cycle
export async function changeBillingCycle(
  subscriptionId: string,
  billingCycle: BillingCycle
): Promise<SubscriptionRecord> {
  const { data, error } = await supabase.rpc("update_billing_cycle", {
    subscription_id: subscriptionId,
    new_billing_cycle: billingCycle
  });

  if (error) {
    console.error("Error changing billing cycle:", error);
    throw error;
  }

  return data as SubscriptionRecord;
}

// Add addon to subscription
export async function addSubscriptionAddon(
  subscriptionId: string,
  addonPackId: string,
  quantity: number = 1
): Promise<SubscriptionAddon> {
  const { data, error } = await supabase.rpc("add_subscription_addon", {
    subscription_id: subscriptionId,
    addon_pack_id: addonPackId,
    quantity
  });

  if (error) {
    console.error("Error adding subscription addon:", error);
    throw error;
  }

  return data as SubscriptionAddon;
}

// Remove addon from subscription
export async function removeSubscriptionAddon(addonId: string): Promise<void> {
  const { data: success, error } = await supabase.rpc("remove_subscription_addon", {
    addon_id: addonId
  });

  if (error) {
    console.error("Error removing subscription addon:", error);
    throw error;
  }
}

// Get subscription users
export async function getSubscriptionUsers(
  subscriptionId: string
): Promise<SubscriptionUser[]> {
  const { data, error } = await supabase.rpc("get_subscription_users", {
    p_subscription_id: subscriptionId
  });

  if (error) {
    console.error("Error fetching subscription users:", error);
    throw error;
  }

  return data as SubscriptionUser[];
}

// Add user to subscription
export async function addUserToSubscription(
  subscriptionId: string,
  userId: string,
  addedBy: string
): Promise<SubscriptionUser> {
  const { data, error } = await supabase.rpc("add_subscription_user", {
    p_subscription_id: subscriptionId,
    p_user_id: userId,
    p_added_by: addedBy
  });

  if (error) {
    console.error("Error adding user to subscription:", error);
    throw error;
  }

  return data as SubscriptionUser;
}

// Remove user from subscription
export async function removeUserFromSubscription(
  subscriptionId: string,
  userId: string
): Promise<void> {
  const { data: success, error } = await supabase.rpc("remove_subscription_user", {
    p_subscription_id: subscriptionId,
    p_user_id: userId
  });

  if (error) {
    console.error("Error removing user from subscription:", error);
    throw error;
  }
}
